import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Plus, Trash2, TrendingUp, X, PoundSterling, AlertCircle, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';
import ValuationWizard from '../components/valuation/ValuationWizard';
import DocumentsPanel from '../components/properties/DocumentsPanel';
import PhotosPanel from '../components/properties/PhotosPanel';
import SharingSettingsPanel from '../components/properties/SharingSettingsPanel';
import EditPropertyModal from '../components/properties/EditPropertyModal';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/api';

const PROPERTY_TYPES = [
  { value: 'detached', label: 'Detached' },
  { value: 'semi_detached', label: 'Semi-detached' },
  { value: 'terraced', label: 'Terraced' },
  { value: 'end_of_terrace', label: 'End of terrace' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'flat', label: 'Flat' },
  { value: 'maisonette', label: 'Maisonette' },
];

const RELATIONSHIPS = [
  { value: 'owner_occupier', label: 'Owner-occupier' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'executor', label: 'Executor' },
  { value: 'developer', label: 'Developer' },
  { value: 'other', label: 'Other' },
];

function AddPropertyForm({ onSave, onCancel }) {
  const [form, setForm] = useState({
    address_line1: '', address_line2: '', town: '', postcode: '',
    property_type: 'detached', bedrooms: 3, bathrooms: 1,
    relationship_to_property: 'owner_occupier', tenure: 'freehold',
    lease_years_remaining: null,
    outstanding_debt: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.address_line1 || !form.town || !form.postcode) {
      toast.error('Please fill in address, town, and postcode');
      return;
    }
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 space-y-4">
      <h3 className="text-white font-semibold">Add Property</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input value={form.address_line1} onChange={e => set('address_line1', e.target.value)}
          placeholder="Address line 1 *" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500" />
        <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)}
          placeholder="Address line 2" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500" />
        <input value={form.town} onChange={e => set('town', e.target.value)}
          placeholder="Town/City *" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500" />
        <input value={form.postcode} onChange={e => set('postcode', e.target.value)}
          placeholder="Postcode *" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <select value={form.property_type} onChange={e => set('property_type', e.target.value)}
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white">
          {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={form.bedrooms} onChange={e => set('bedrooms', parseInt(e.target.value))}
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white">
          {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n} bed</option>)}
        </select>
        <select value={form.relationship_to_property} onChange={e => set('relationship_to_property', e.target.value)}
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white">
          {RELATIONSHIPS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select value={form.tenure} onChange={e => set('tenure', e.target.value)}
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white">
          <option value="freehold">Freehold</option>
          <option value="leasehold">Leasehold</option>
        </select>
      </div>

      {form.tenure === 'leasehold' && (
        <input type="number" value={form.lease_years_remaining || ''} onChange={e => set('lease_years_remaining', parseInt(e.target.value) || null)}
          placeholder="Years remaining on lease" className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 w-48" />
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">
          Outstanding debt (£) <span className="text-slate-600">(mortgage / bridging / secured loan balance — optional)</span>
        </label>
        <input
          type="number" min="0" step="100"
          value={form.outstanding_debt}
          onChange={e => set('outstanding_debt', e.target.value)}
          placeholder="e.g. 145000"
          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 w-64"
        />
        <p className="text-[10px] text-slate-600 mt-1">
          Used to calculate Loan-to-Value (LTV) against this property's indicative value range.
        </p>
      </div>

      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition">Cancel</button>
        <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium">Save Property</button>
      </div>
    </form>
  );
}

function SummaryCard({ label, value, sub, tone = 'slate' }) {
  const valueClass = {
    slate:   'text-white',
    emerald: 'text-emerald-300',
    amber:   'text-amber-300',
    rose:    'text-rose-300',
    blue:    'text-blue-300',
  }[tone] || 'text-white';
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${valueClass} mt-1`}>{value}</div>
      <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>
    </div>
  );
}

export default function MyProperties() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [valuatingId, setValuatingId] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [vaultSummary, setVaultSummary] = useState(null);

  // Brokers / "Share with Broker" / portfolio metrics are investor concerns.
  // Providers (auction houses, estate agents, deal sourcers) use the same
  // page as a list of listings they've uploaded — they don't need broker UX.
  const isInvestor =
    user?.is_superuser ||
    !user?.subscription_tier ||
    user?.subscription_tier === 'investor';

  const fetchProperties = () => {
    authFetch('/api/valuation/properties')
      .then(r => r.ok ? r.json() : [])
      .then(setProperties)
      .finally(() => setLoading(false));
  };

  useEffect(fetchProperties, []);

  useEffect(() => {
    if (!isInvestor) return;
    authFetch('/api/documents/personal')
      .then((r) => (r.ok ? r.json() : []))
      .then((groups) => {
        const allDocs = groups.flatMap((g) => g.documents || []);
        setVaultSummary({
          count: allDocs.length,
          expiring: allDocs.filter((d) => d.status === 'expiring_soon').length,
          expired: allDocs.filter((d) => d.status === 'expired').length,
        });
      });
  }, [isInvestor]);

  const handleAdd = async (form) => {
    try {
      const payload = {
        ...form,
        outstanding_debt: form.outstanding_debt ? parseFloat(form.outstanding_debt) : null,
      };
      const res = await authFetch('/api/valuation/properties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Property added');
        setShowAdd(false);
        fetchProperties();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add property');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this property from your portfolio?')) return;
    await authFetch(`/api/valuation/properties/${id}`, { method: 'DELETE' });
    fetchProperties();
  };

  if (valuatingId) {
    const prop = properties.find(p => p.id === valuatingId);
    return (
      <ValuationWizard
        property={prop}
        onClose={() => { setValuatingId(null); fetchProperties(); }}
      />
    );
  }

  // Portfolio aggregates: sum the latest-valuation low/mid/high across every
  // property + total outstanding debt + LTV at each end of the range.
  const portfolio = (() => {
    let lowSum = 0, midSum = 0, highSum = 0, debtSum = 0;
    let valued = 0;
    for (const p of properties) {
      if (p.outstanding_debt) debtSum += Number(p.outstanding_debt);
      const v = p.latest_valuation;
      if (v && (v.range_low || v.range_mid || v.range_high)) {
        valued += 1;
        if (v.range_low)  lowSum  += Number(v.range_low);
        if (v.range_mid)  midSum  += Number(v.range_mid);
        if (v.range_high) highSum += Number(v.range_high);
      }
    }
    const ltvHigh = lowSum > 0  ? (debtSum / lowSum)  * 100 : null;
    const ltvLow  = highSum > 0 ? (debtSum / highSum) * 100 : null;
    return { count: properties.length, valued, lowSum, midSum, highSum, debtSum, ltvLow, ltvHigh };
  })();

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
            <Home size={20} className="text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">My Properties</h1>
            <p className="text-slate-400 text-sm">Properties you own or manage</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            {showAdd ? <X size={14} /> : <Plus size={14} />}
            {showAdd ? 'Cancel' : 'Add Property'}
          </button>
        </div>
      </div>

      {isInvestor && vaultSummary && (
        <Link
          to="/account/identity"
          className="block bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 hover:border-emerald-700"
        >
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-300">
              <strong className="text-white">Identity vault:</strong>{' '}
              {vaultSummary.count === 0
                ? 'no documents yet — add your verification docs to share with the broker'
                : (
                    <>
                      {vaultSummary.count} {vaultSummary.count === 1 ? 'document' : 'documents'}
                      {vaultSummary.expiring > 0 && (
                        <> · <span className="text-amber-300">{vaultSummary.expiring} expiring soon</span></>
                      )}
                      {vaultSummary.expired > 0 && (
                        <> · <span className="text-rose-300">{vaultSummary.expired} expired</span></>
                      )}
                    </>
                  )}
            </div>
            <span className="text-emerald-400 text-sm">Manage →</span>
          </div>
        </Link>
      )}

      {isInvestor && <SharingSettingsPanel />}

      {editingProperty && (
        <EditPropertyModal
          property={editingProperty}
          onClose={() => setEditingProperty(null)}
          onSaved={() => {
            setEditingProperty(null);
            fetchProperties();
          }}
        />
      )}

      {/* Portfolio summary strip — only meaningful once at least one property
          has a valuation. Shows the value range, total outstanding debt, and
          LTV at each end of the range so the user can see best/worst-case
          gearing across the portfolio. */}
      {portfolio.count > 0 && portfolio.valued > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard label="Portfolio (low)" value={formatCurrency(portfolio.lowSum)}
            sub={`${portfolio.valued} valued · conservative`} />
          <SummaryCard label="Portfolio (high)" value={formatCurrency(portfolio.highSum)}
            sub="best-case range" tone="emerald" />
          <SummaryCard label="Outstanding debt" value={formatCurrency(portfolio.debtSum)}
            sub={portfolio.debtSum > 0 ? 'across portfolio' : 'none recorded'} tone="amber" />
          <SummaryCard
            label="Portfolio LTV"
            value={portfolio.debtSum > 0 && portfolio.ltvLow != null
              ? `${portfolio.ltvLow.toFixed(0)}% – ${portfolio.ltvHigh.toFixed(0)}%`
              : '—'}
            sub={portfolio.debtSum > 0 ? 'at high → low value' : 'no debt recorded'}
            tone={portfolio.ltvHigh > 80 ? 'rose' : 'blue'}
          />
        </div>
      )}

      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <AddPropertyForm onSave={handleAdd} onCancel={() => setShowAdd(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="text-center py-20 text-slate-500">Loading...</div>
      ) : properties.length === 0 ? (
        <div className="text-center py-20">
          <Home size={48} className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No properties in your portfolio yet</p>
          <p className="text-slate-600 text-sm mt-1">Add a property to get started with valuations</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {properties.map(prop => (
            <motion.div
              key={prop.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-5"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-white font-medium">{prop.address_line1}</h3>
                  <p className="text-slate-500 text-sm">{prop.town}, {prop.postcode}</p>
                  <p className="text-slate-600 text-xs mt-1">
                    {prop.property_type} · {prop.bedrooms} bed · {prop.tenure}
                    {prop.relationship_to_property && ` · ${prop.relationship_to_property.replace('_', ' ')}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingProperty(prop)}
                    className="text-slate-600 hover:text-emerald-400 transition"
                    title="Edit this property"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(prop.id)}
                    className="text-slate-600 hover:text-red-400 transition"
                    title="Remove from portfolio"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {prop.latest_valuation ? (
                <div className="bg-slate-800/60 rounded-lg p-3 mb-3">
                  <div className="text-xs text-slate-500 mb-1">Latest Valuation</div>
                  <div className="text-lg font-bold text-emerald-400">
                    {formatCurrency(prop.latest_valuation.range_low)} — {formatCurrency(prop.latest_valuation.range_high)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Midpoint: {formatCurrency(prop.latest_valuation.range_mid)}
                    {prop.latest_valuation.created_at && (
                      <span> · {new Date(prop.latest_valuation.created_at).toLocaleDateString('en-GB')}</span>
                    )}
                  </div>
                  {prop.outstanding_debt > 0 && prop.latest_valuation.range_high && (() => {
                    const ltvLow  = (prop.outstanding_debt / prop.latest_valuation.range_high) * 100;
                    const ltvHigh = (prop.outstanding_debt / prop.latest_valuation.range_low)  * 100;
                    const tone = ltvHigh > 80 ? 'text-rose-300'
                              : ltvHigh > 65 ? 'text-amber-300'
                              : 'text-blue-300';
                    return (
                      <div className={`mt-2 pt-2 border-t border-slate-700/40 text-xs ${tone}`}>
                        <PoundSterling size={11} className="inline" /> Debt {formatCurrency(prop.outstanding_debt)}
                        {' · '}
                        <strong>LTV {ltvLow.toFixed(0)}% – {ltvHigh.toFixed(0)}%</strong>
                      </div>
                    );
                  })()}
                </div>
              ) : prop.outstanding_debt > 0 ? (
                <div className="bg-slate-800/60 rounded-lg p-3 mb-3 text-xs text-slate-400 flex items-center gap-2">
                  <AlertCircle size={12} className="text-amber-400" />
                  Debt {formatCurrency(prop.outstanding_debt)} recorded — get a valuation to see LTV.
                </div>
              ) : null}

              <button
                onClick={() => setValuatingId(prop.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
              >
                <TrendingUp size={14} />
                {prop.latest_valuation ? 'Recalculate Value' : 'Get an Indicative Value'}
              </button>

              <PhotosPanel userPropertyId={prop.id} />
              <DocumentsPanel userPropertyId={prop.id} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
